/**
 * Capacités d'aperçu document côté navigateur.
 * Les PDF en {@code <iframe src="blob:…">} sont peu fiables sur WebKit / mobile.
 */

import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Observable } from 'rxjs';

export type ViewerKind = 'pdf' | 'image' | 'other';

/** Messages i18n déjà résolus (instant) pour la session viewer. */
export interface DocumentViewerMessages {
  viewError: string;
  viewPdfMobile: string;
  documentFallback: string;
}

/** True si un PDF peut être affiché inline (iframe) de façon raisonnable. */
export function canPreviewPdfInline(): boolean {
  if (typeof navigator === 'undefined') {
    return true;
  }
  const ua = navigator.userAgent || '';
  // iPadOS se présente souvent comme Macintosh + écran tactile.
  const isIos =
    /iPad|iPhone|iPod/i.test(ua) ||
    (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/i.test(ua);
  return !isIos && !isAndroid;
}

/**
 * Ouvre un onglet vide synchronement (geste utilisateur) pour y charger
 * le PDF ensuite — contournement du bloqueur de popups iOS/Android.
 */
export function openBlankPreviewTab(): Window | null {
  return window.open('about:blank', '_blank');
}

/** Navigue un onglet déjà ouvert vers l'URL blob du PDF. */
export function navigatePreviewTab(
  previewTab: Window | null | undefined,
  objectUrl: string | undefined
): boolean {
  if (previewTab?.closed !== false || !objectUrl?.startsWith('blob:')) {
    return false;
  }
  try {
    previewTab.location.href = objectUrl;
    return true;
  } catch {
    return false;
  }
}

export function closePreviewTab(previewTab: Window | null | undefined): void {
  if (previewTab?.closed === false) {
    previewTab.close();
  }
}

/**
 * Ouvre une URL blob locale dans un nouvel onglet (lecteur PDF natif).
 * Préférer {@link openBlankPreviewTab} + {@link navigatePreviewTab} après un clic.
 */
export function openBlobInNewTab(objectUrl: string | undefined): boolean {
  if (!objectUrl?.startsWith('blob:')) {
    return false;
  }
  const opened = window.open(objectUrl, '_blank');
  return opened != null;
}

export function resolveViewerKind(contentType: string): ViewerKind {
  const type = contentType.toLowerCase();
  if (type.startsWith('image/')) {
    return 'image';
  }
  if (type.includes('pdf')) {
    return 'pdf';
  }
  return 'other';
}

export function coerceTypedBlob(blob: Blob, fallbackType: string): Blob {
  if (blob.type && blob.type !== 'application/octet-stream') {
    return blob;
  }
  return new Blob([blob], { type: fallbackType });
}

/**
 * État + pipeline d'aperçu blob (desktop iframe / mobile onglet natif).
 * Une instance par composant hôte.
 */
export class DocumentViewerSession {
  loading = false;
  errorMessage: string | null = null;
  title = '';
  fileName = '';
  kind: ViewerKind = 'other';
  safeUrl?: SafeResourceUrl;
  /** False sur mobile : iframe PDF blob peu fiable → ouverture native. */
  pdfInline = true;
  private objectUrl?: string;

  constructor(private readonly sanitizer: DomSanitizer) {}

  open(options: {
    title: string;
    fileName?: string | null;
    contentType?: string | null;
    download$: Observable<Blob>;
    dialog?: HTMLDialogElement;
    messages: DocumentViewerMessages;
    resolveErrorAsync: (err: unknown) => Promise<string>;
  }): void {
    if (this.loading) {
      return;
    }

    const { title, download$, dialog, messages, resolveErrorAsync } = options;
    this.loading = true;
    this.errorMessage = null;
    this.title = title;
    this.fileName = options.fileName?.trim() || messages.documentFallback;
    this.revoke();

    const fallbackType = options.contentType?.trim() || 'application/pdf';
    const preferExternalPdf =
      !canPreviewPdfInline() && resolveViewerKind(fallbackType) === 'pdf';
    const previewTab = preferExternalPdf ? openBlankPreviewTab() : null;
    if (!preferExternalPdf) {
      dialog?.showModal();
    }

    download$.subscribe({
      next: (blob) => {
        const typedBlob = coerceTypedBlob(blob, fallbackType);
        this.kind = resolveViewerKind(typedBlob.type || fallbackType);
        this.pdfInline = this.kind !== 'pdf' || canPreviewPdfInline();
        const objectUrl = URL.createObjectURL(typedBlob);
        const trustedUrl = this.trustLocalBlobUrl(objectUrl);
        if (!trustedUrl) {
          URL.revokeObjectURL(objectUrl);
          closePreviewTab(previewTab);
          this.loading = false;
          dialog?.showModal();
          this.errorMessage = messages.viewError;
          return;
        }
        this.objectUrl = objectUrl;
        this.safeUrl = trustedUrl;
        this.loading = false;

        if (!this.pdfInline && this.kind === 'pdf') {
          if (navigatePreviewTab(previewTab, objectUrl)) {
            return;
          }
          closePreviewTab(previewTab);
          if (openBlobInNewTab(objectUrl)) {
            return;
          }
          dialog?.showModal();
          this.errorMessage = messages.viewPdfMobile;
          return;
        }

        closePreviewTab(previewTab);
        if (preferExternalPdf) {
          dialog?.showModal();
        }
      },
      error: (err) => {
        closePreviewTab(previewTab);
        this.loading = false;
        dialog?.showModal();
        void resolveErrorAsync(err).then((message) => {
          if (dialog?.open) {
            this.errorMessage = message;
          }
        });
      },
    });
  }

  close(dialog?: HTMLDialogElement): void {
    dialog?.close();
    this.loading = false;
    this.errorMessage = null;
    this.title = '';
    this.fileName = '';
    this.kind = 'other';
    this.safeUrl = undefined;
    this.pdfInline = true;
    // Ne pas révoquer ici : un onglet mobile peut encore utiliser le blob.
  }

  onDialogCancel(event: Event, dialog?: HTMLDialogElement): void {
    event.preventDefault();
    this.close(dialog);
  }

  openExternally(viewError: string): void {
    if (!openBlobInNewTab(this.objectUrl)) {
      this.errorMessage = viewError;
    }
  }

  downloadFile(documentFallback: string): void {
    if (!this.objectUrl) {
      return;
    }
    if (!canPreviewPdfInline()) {
      openBlobInNewTab(this.objectUrl);
      return;
    }
    const anchor = document.createElement('a');
    anchor.href = this.objectUrl;
    anchor.download = this.fileName || documentFallback;
    anchor.rel = 'noopener';
    anchor.click();
  }

  revoke(): void {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = undefined;
    }
  }

  private trustLocalBlobUrl(objectUrl: string): SafeResourceUrl | null {
    if (!objectUrl.startsWith('blob:')) {
      return null;
    }
    // Safe: local blob: URL only — required for <iframe>/<img> [src] binding.
    return this.sanitizer.bypassSecurityTrustResourceUrl(objectUrl); // NOSONAR
  }
}
